import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import imageCompression from 'browser-image-compression';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import {
  Box,
  Grid,
  Stack,
  Link,
  Divider,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
  IconButton,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import tracker from '../../../api/tracker';
import ImageUploaderWithPreview from '../../../components/hook-form/ImageUploaderWithPreview';
import Iconify from '../../../components/Iconify';

// ----------------------------------------------------------------------

UserForm.propTypes = {
  user: PropTypes.shape({
    hashid: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    role_hash: PropTypes.string,
    picture: PropTypes.any,
  }),
};

UserForm.defaultProps = {
  user: undefined,
};

export default function UserForm({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.role === 'Administrator';

  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roleList, setRoleList] = useState([]);

  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchRoleData = async () => {
    const result = await tracker.get('roles');
    setRoleList(result.data.data);
  };

  useEffect(() => {
    if (user) {
      const fields = ['email', 'name', 'hashid', 'role_hash'];
      fields.forEach((field) => setValue(field, user[field]));
    }
    fetchRoleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const UserSchema = Yup.object().shape({
    name: Yup.string().required(t('Required')),
    // password: Yup.string().required(t('Required')),
    email: Yup.string().email(t('Valid_email')).required('Required'),
    role_hash: Yup.string().required(t('Required')),
    picture: Yup.mixed().test('fileSize', 'The file is too large', (value) => {
      console.log(value);
      return true;
    }),
  });

  const defaultValues = {
    name: '',
    password: '',
    email: '',
    role_hash: '',
    picture: null,
  };

  const methods = useForm({
    resolver: yupResolver(UserSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    if (data.picture instanceof File || data.picture instanceof Blob) {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(data.picture, options);
      data.picture = compressedFile;
    } else data.picture = null;

    if (!user)
      await tracker.post('register', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    else
      await tracker.post(`users/update/${user.hashid}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    navigate('/dashboard/user', { replace: true });
  };

  // Password validation helpers
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword && newPassword !== confirmPassword;

  const handleChangePassword = async () => {
    if (!passwordCurrent) {
      enqueueSnackbar(t('La contraseña actual es requerida'), { variant: 'error' });
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      enqueueSnackbar(t('La nueva contraseña debe tener al menos 6 caracteres'), { variant: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      enqueueSnackbar(t('Las nuevas contraseñas no coinciden'), { variant: 'error' });
      return;
    }

    setChangingPassword(true);
    try {
      await tracker.post(`users/change-password/${user.hashid}`, {
        current_password: passwordCurrent,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      enqueueSnackbar(t('Contraseña actualizada correctamente'), { variant: 'success' });
      setPasswordCurrent('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.message
        || error.response?.data?.errors?.[Object.keys(error.response?.data?.errors || {})[0]]?.[0]
        || t('something_went_wrong');
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        {/* LEFT: Main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Name field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('Nombre')}</Typography>
              </Stack>
              <RHFTextField name="name" label={t('Nombre')} fullWidth />
            </Box>

            {/* Role field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <AdminPanelSettingsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('Role')}</Typography>
              </Stack>
              <FormControl fullWidth>
                <InputLabel id="role-hash-label">{t('Role')}</InputLabel>
                <Select
                  labelId="role-hash-label"
                  id="role-hash"
                  name="role_hash"
                  value={watch('role_hash') || ''}
                  label={t('Role')}
                  onChange={e => methods.setValue('role_hash', e.target.value)}
                >
                  {roleList && roleList.map(option => (
                    <MenuItem key={option.value || option.hashid || option} value={option.value || option.hashid || option}>
                      {option.label || option.name || option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Email field */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('Correo')}</Typography>
              </Stack>
              <RHFTextField name="email" label={t('Correo')} fullWidth />
            </Box>

            <input type="hidden" name="hashid" />

            {/* Password field (only for new users) */}
            {!user ? (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <LockIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">{t('Contraseña')}</Typography>
                </Stack>
                <RHFTextField
                  name="password"
                  label={t('Contraseña')}
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            ) : (
              false
            )}

            {/* Profile picture upload */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <ImageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" color="text.secondary">{t('profile_picture') || 'Foto de perfil'}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {t('profile_picture_hint') || 'Sube una imagen de perfil. Se comprimirá automáticamente.'}
              </Typography>
              <ImageUploaderWithPreview name="picture" currentUrl={user?.picture || ''} aspectRatio="1/1" />
            </Box>

            {/* Password Change Section - Only for Administrators when editing */}
            {user && isAdmin && (
              <Box
                sx={{
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  background: '#fff8e1',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <LockIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                  <Typography variant="h6" color="warning.dark" fontWeight={700}>
                    {t('change_password_section') || 'Cambiar Contraseña'}
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2.5}>
                  {/* Current Password (admin's own password) */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                      {t('current_password_label') || 'Tu contraseña actual (para verificar identidad)'}
                    </Typography>
                    <TextField
                      label={t('Contraseña actual') || 'Contraseña actual'}
                      fullWidth
                      value={passwordCurrent}
                      onChange={(e) => setPasswordCurrent(e.target.value)}
                      type={showCurrentPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                              <Iconify icon={showCurrentPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* New Password */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                      {t('Nueva contraseña') || 'Nueva contraseña'}
                    </Typography>
                    <TextField
                      label={t('Nueva contraseña') || 'Nueva contraseña'}
                      fullWidth
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      type={showNewPassword ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" onClick={() => setShowNewPassword(!showNewPassword)}>
                              <Iconify icon={showNewPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {newPassword && newPassword.length < 6 && (
                      <Typography variant="caption" color="error" mt={0.5}>
                        {t('password_min_length') || 'Mínimo 6 caracteres'}
                      </Typography>
                    )}
                  </Box>

                  {/* Confirm New Password */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                      {t('Confirmar nueva contraseña') || 'Confirmar nueva contraseña'}
                    </Typography>
                    <TextField
                      label={t('Confirmar nueva contraseña') || 'Confirmar nueva contraseña'}
                      fullWidth
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      type={showConfirmPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: confirmPassword ? (
                          <InputAdornment position="start">
                            {passwordsMatch && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
                            {passwordsMismatch && <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />}
                          </InputAdornment>
                        ) : undefined,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {passwordsMatch && (
                      <Typography variant="caption" color="success.main" mt={0.5} display="flex" alignItems="center" gap={0.5}>
                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                        {t('passwords_match') || 'Las contraseñas coinciden'}
                      </Typography>
                    )}
                    {passwordsMismatch && (
                      <Typography variant="caption" color="error" mt={0.5} display="flex" alignItems="center" gap={0.5}>
                        <CancelIcon sx={{ fontSize: 14 }} />
                        {t('passwords_do_not_match') || 'Las contraseñas no coinciden'}
                      </Typography>
                    )}
                  </Box>

                  <LoadingButton
                    fullWidth
                    size="medium"
                    variant="contained"
                    color="warning"
                    loading={changingPassword}
                    onClick={handleChangePassword}
                    sx={{ borderRadius: 2 }}
                  >
                    {t('update_password') || 'Actualizar Contraseña'}
                  </LoadingButton>
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* RIGHT: Actions panel */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              background: '#f8faff',
              position: 'sticky',
              top: 80,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              {t('actions') || 'Acciones'}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ background: '#032C65', '&:hover': { background: '#054a9e' }, borderRadius: 2 }}
              >
                {t('Save')}
              </LoadingButton>
              <Link
                component={RouterLink}
                variant="body2"
                underline="hover"
                to="/dashboard/user/"
                sx={{ textAlign: 'center', display: 'block', color: 'text.secondary' }}
              >
                {t('go_back')}
              </Link>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
}